import React, { useEffect, useState } from "react";
import { useFormContext, useFieldArray } from "react-hook-form";
import { RiDeleteBin5Line } from "react-icons/ri";

import { AddMore } from "../../../../../assets";
import {
  DISTANCE_UNIT,
  distanceUnitOptions,
  EMPTY_STRING,
  locationTypes,
  MILES_TO_KM,
  ZERO_VALUE,
} from "../../../../../constants";
import CustomSvg from "../../../../../shared/components/custom-svg/CustomSvg";
import CustomSelect from "../../../../../shared/components/select/CustomSelect";
import { formatToTwoDecimalPlaces } from "../../../../../shared/utility";

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const milesToKm = (miles) => {
  const milesValue = toNumber(miles);
  if (milesValue === null || milesValue < ZERO_VALUE) {
    return null;
  }
  return Number(formatToTwoDecimalPlaces(milesValue * MILES_TO_KM));
};

const kmToMiles = (km) => {
  const kmValue = toNumber(km);
  if (kmValue === null || kmValue < ZERO_VALUE) {
    return null;
  }
  return Number(formatToTwoDecimalPlaces(kmValue / MILES_TO_KM));
};

const NearbyLocationsForm = () => {
  const {
    register,
    control,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "amenity.nearbyLocations",
  });

  const [errorFields, setErrorFields] = useState([]);
  const [currentLocation, setCurrentLocation] = useState(1);

  const changeCurrentLocation = (id) => {
    setCurrentLocation(id);
  };

  const handleAddNewLocation = () => {
    append({
      key: Date.now(),
      type: currentLocation,
      name: EMPTY_STRING,
      distance: ZERO_VALUE,
      distanceKm: ZERO_VALUE,
      distanceUnit: DISTANCE_UNIT.MILES,
    });
  };

  const getFieldIndex = (field) =>
    fields.findIndex((loc) => (loc.id ?? loc.key) === (field.id ?? field.key));

  const getDisplayDistance = (fieldIndex) => {
    const unit = Number(
      watch(`amenity.nearbyLocations.${fieldIndex}.distanceUnit`)
    );
    if (unit === DISTANCE_UNIT.KILOMETERS) {
      return (
        watch(`amenity.nearbyLocations.${fieldIndex}.distanceKm`) ??
        ZERO_VALUE
      );
    }
    return (
      watch(`amenity.nearbyLocations.${fieldIndex}.distance`) ?? ZERO_VALUE
    );
  };

  const applyDistanceForUnit = (fieldIndex, inputValue, unit) => {
    if (unit === DISTANCE_UNIT.KILOMETERS) {
      setValue(
        `amenity.nearbyLocations.${fieldIndex}.distanceKm`,
        inputValue,
        { shouldValidate: true }
      );
      const nextMiles = kmToMiles(inputValue);
      setValue(
        `amenity.nearbyLocations.${fieldIndex}.distance`,
        nextMiles === null ? ZERO_VALUE : nextMiles,
        { shouldValidate: true }
      );
      return;
    }

    setValue(`amenity.nearbyLocations.${fieldIndex}.distance`, inputValue, {
      shouldValidate: true,
    });
    const nextKm = milesToKm(inputValue);
    setValue(
      `amenity.nearbyLocations.${fieldIndex}.distanceKm`,
      nextKm === null ? ZERO_VALUE : nextKm,
      { shouldValidate: true }
    );
  };

  const handleDistanceChange = (fieldIndex, event) => {
    const inputValue = event.target.value;
    const unit = Number(
      watch(`amenity.nearbyLocations.${fieldIndex}.distanceUnit`)
    );
    applyDistanceForUnit(fieldIndex, inputValue, unit);
  };

  const handleUnitChange = (fieldIndex, selectedOption) => {
    const nextUnit = selectedOption?.value;
    if (nextUnit == null) {
      return;
    }

    const currentDisplay = getDisplayDistance(fieldIndex);
    applyDistanceForUnit(fieldIndex, currentDisplay, nextUnit);
  };

  useEffect(() => {
    const currentErrorFields = fields
      .map((field, index) => {
        const error = errors?.amenity?.nearbyLocations?.[index];
        return error ? { index, field } : null;
      })
      .filter(Boolean);
    setErrorFields(currentErrorFields.map((field) => field.field.type));

    // eslint-disable-next-line
  }, [currentLocation, errors, fields]);

  return (
    <div className="main-container-form">
      <div className="location-types">
        {locationTypes.map((locationType) => (
          <div
            key={locationType.id}
            className={`location-type ${
              currentLocation === locationType.id
                ? "selected-location"
                : errorFields.includes(locationType.id)
                ? "text-danger"
                : EMPTY_STRING
            }`}
            onClick={() => changeCurrentLocation(locationType.id)}
          >
            <CustomSvg
              src={locationType.icon}
              className="agent-create-listing-nearby-location-icon"
            />
            <div className="location-type-name">{locationType.name}</div>
          </div>
        ))}
      </div>

      {errors?.amenity?.nearbyLocations?.message && (
        <div className="error mt-2">
          {errors?.amenity?.nearbyLocations.message}
        </div>
      )}

      <div className="row locations-fields" id="fields">
        {fields
          .filter((location) => location.type === currentLocation)
          .map((field) => {
            const fieldIndex = getFieldIndex(field);
            const fieldErrors = errors?.amenity?.nearbyLocations?.[fieldIndex];

            return (
              <React.Fragment key={field.id ?? field.key}>
                <div className="col-12 col-sm-5 col-md-5 location-input-field">
                  <label>Name:</label>
                  <input
                    type="text"
                    className={`create-list-input ${
                      fieldErrors?.name ? "border border-danger" : EMPTY_STRING
                    }`}
                    placeholder="Name"
                    value={watch(`amenity.nearbyLocations.${fieldIndex}.name`)}
                    {...register(`amenity.nearbyLocations.${fieldIndex}.name`)}
                  />
                </div>
                <div className="col-12 col-sm-3 col-md-3 location-input-field">
                  <label>Distance:</label>
                  <input
                    type="text"
                    className={`create-list-input ${
                      fieldErrors?.distance || fieldErrors?.distanceKm
                        ? "border border-danger"
                        : EMPTY_STRING
                    }`}
                    value={getDisplayDistance(fieldIndex)}
                    placeholder="Distance"
                    onChange={(event) => handleDistanceChange(fieldIndex, event)}
                  />
                </div>
                <div className="col-12 col-sm-3 col-md-3 location-input-field">
                  <label>Unit:</label>
                  <CustomSelect
                    options={distanceUnitOptions}
                    name={`amenity.nearbyLocations.${fieldIndex}.distanceUnit`}
                    placeholder="Unit"
                    showError={false}
                    isClearable={false}
                    defaultStyle={false}
                    customHandleChange={(selectedOption) =>
                      handleUnitChange(fieldIndex, selectedOption)
                    }
                  />
                </div>
                <input
                  type="hidden"
                  {...register(`amenity.nearbyLocations.${fieldIndex}.type`)}
                  value={field.type}
                />
                <input
                  type="hidden"
                  {...register(
                    `amenity.nearbyLocations.${fieldIndex}.distance`
                  )}
                />
                <input
                  type="hidden"
                  {...register(
                    `amenity.nearbyLocations.${fieldIndex}.distanceKm`
                  )}
                />
                <div className="col-12 col-sm-1 col-md-1 d-flex justify-content-center align-items-center">
                  <RiDeleteBin5Line
                    size={14}
                    onClick={() => remove(fieldIndex)}
                    className="mt-2 text-danger pointer"
                  />
                </div>
              </React.Fragment>
            );
          })}
      </div>
      <div
        className="add-more-button pointer d-flex align-items-center gap-2"
        onClick={handleAddNewLocation}
      >
        <img src={AddMore} alt="Add More Icon" />
        <div className="add-more-text">Add More</div>
      </div>
    </div>
  );
};

export default NearbyLocationsForm;
